import React, { useEffect, useState, useRef } from 'react';
import { motion, useAnimation, AnimatePresence } from 'framer-motion';
interface BubbleButtonProps {
  onClick?: () => void;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  color?: 'green' | 'red' | 'blue' | 'yellow';
  size?: 'sm' | 'md' | 'lg';
}
export const BubbleButton = ({
  onClick,
  children,
  className = '',
  disabled = false,
  color = 'green',
  size = 'md'
}: BubbleButtonProps) => {
  const [bubbles, setBubbles] = useState<Array<{
    id: number;
    x: number;
    y: number;
    size: number;
  }>>([]);
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const glowAnimation = useAnimation();
  const chargeAnimation = useAnimation();
  // Color configurations
  const colorConfig = {
    green: {
      base: 'bg-green-500',
      hover: 'bg-green-400',
      glow: 'rgba(72, 255, 128, 0.7)',
      border: 'border-green-400',
      shadow: 'shadow-green-500/30',
      scanLine: 'rgba(72, 255, 128, 0.15)'
    },
    red: {
      base: 'bg-red-500',
      hover: 'bg-red-400',
      glow: 'rgba(255, 72, 72, 0.7)',
      border: 'border-red-400',
      shadow: 'shadow-red-500/30',
      scanLine: 'rgba(255, 72, 72, 0.15)'
    },
    blue: {
      base: 'bg-blue-500',
      hover: 'bg-blue-400',
      glow: 'rgba(72, 128, 255, 0.7)',
      border: 'border-blue-400',
      shadow: 'shadow-blue-500/30',
      scanLine: 'rgba(72, 128, 255, 0.15)'
    },
    yellow: {
      base: 'bg-yellow-500',
      hover: 'bg-yellow-400',
      glow: 'rgba(255, 212, 72, 0.7)',
      border: 'border-yellow-400',
      shadow: 'shadow-yellow-500/30',
      scanLine: 'rgba(255, 212, 72, 0.15)'
    }
  };
  const sizeClasses = {
    sm: 'py-1 px-2 text-sm',
    md: 'py-2 px-4',
    lg: 'py-3 px-6 text-lg'
  };
  useEffect(() => {
    if (isHovered && !disabled) {
      // Start the charging animation
      chargeAnimation.start({
        width: '100%',
        transition: {
          duration: 1.5,
          ease: 'easeInOut'
        }
      });
      // Start the pulsing glow animation
      glowAnimation.start({
        boxShadow: [`0 0 5px ${colorConfig[color].glow}`, `0 0 15px ${colorConfig[color].glow}`, `0 0 5px ${colorConfig[color].glow}`],
        transition: {
          duration: 2,
          repeat: Infinity,
          repeatType: 'loop',
          ease: 'easeInOut'
        }
      });
    } else {
      // Reset animations
      chargeAnimation.start({
        width: '0%'
      });
      glowAnimation.stop();
    }
  }, [isHovered, disabled, color, chargeAnimation, glowAnimation]);
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) return;
    setIsPressed(true);
    setTimeout(() => setIsPressed(false), 300);
    // Create multiple bubbles at click position with varying sizes
    const button = e.currentTarget;
    const rect = button.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    // Create 5 bubbles with different sizes
    const newBubbles = Array.from({
      length: 5
    }, (_, i) => ({
      id: Date.now() + i,
      x,
      y,
      size: 5 + Math.random() * 10 // Random size between 5-15px
    }));
    setBubbles(prev => [...prev, ...newBubbles]);
    // Remove bubbles after animation completes
    setTimeout(() => {
      setBubbles(prev => prev.filter(bubble => !newBubbles.some(nb => nb.id === bubble.id)));
    }, 700);
    if (onClick) onClick();
  };
  return <motion.button ref={buttonRef} whileTap={{
    scale: 0.92
  }} whileHover={{
    scale: 1.04
  }} animate={glowAnimation} onClick={handleClick} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} disabled={disabled} className={`relative overflow-hidden font-mono ${sizeClasses[size]} 
        ${colorConfig[color].base} hover:${colorConfig[color].hover} text-black 
        transition-all border border-transparent hover:${colorConfig[color].border}
        disabled:opacity-50 disabled:cursor-not-allowed ${className}`} style={{
    transformStyle: 'preserve-3d'
  }} data-id="element-13">
      {/* Background scan lines - cyber effect */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{
      backgroundImage: `repeating-linear-gradient(
            0deg,
            transparent,
            transparent 2px,
            ${colorConfig[color].scanLine} 3px,
            transparent 3px
          )`,
      backgroundSize: '100% 5px',
      opacity: isHovered ? 1 : 0,
      transition: 'opacity 0.3s ease'
    }} data-id="element-14" />
      {/* Charging bar */}
      <motion.div animate={chargeAnimation} className="absolute bottom-0 left-0 h-[2px] bg-white" style={{
      width: '0%',
      opacity: 0.7
    }} data-id="element-15" />
      {/* Bubble effects */}
      <AnimatePresence data-id="element-16">
        {bubbles.map(bubble => <motion.span key={bubble.id} initial={{
        scale: 0,
        opacity: 1
      }} animate={{
        scale: 15,
        opacity: 0
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.7,
        ease: 'easeOut'
      }} className="absolute rounded-full bg-white" style={{
        left: bubble.x,
        top: bubble.y,
        width: `${bubble.size}px`,
        height: `${bubble.size}px`
      }} data-id="element-17" />)}
      </AnimatePresence>
      {/* Digital noise effect on press */}
      {isPressed && <motion.div initial={{
      opacity: 0.7
    }} animate={{
      opacity: 0
    }} transition={{
      duration: 0.3
    }} className="absolute inset-0 pointer-events-none" style={{
      backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 150 150' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
      mixBlendMode: 'overlay'
    }} data-id="element-18" />}
      {/* Border glow effect on hover */}
      {isHovered && !disabled && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="absolute inset-0 pointer-events-none" style={{
      boxShadow: `inset 0 0 0 1px ${colorConfig[color].glow}`,
      borderRadius: 'inherit'
    }} data-id="element-19" />}
      {/* Content wrapper with 3D effect */}
      <motion.div animate={{
      y: isPressed ? 2 : 0,
      rotateX: isPressed ? '-10deg' : '0deg'
    }} transition={{
      type: 'spring',
      stiffness: 300,
      damping: 15
    }} className="relative z-10 flex items-center justify-center" data-id="element-20">
        {children}
      </motion.div>
    </motion.button>;
};